import React from "react";
import TransactionType from "../../TransactionType";
import dayjs from 'dayjs';
import { Trans } from "@lingui/macro";
import AmountTd from "../Common/AmountTd";
import TransactionStatus from '../../TransactionStatus';
import InvoiceTransactionStatus from "../../TransactionStatus/Invoice";
import TransactionEmailStatus from "../../TransactionEmailStatus";
import {Type} from "bukku/redux/Transaction/helper";
import TransactionListMenu from "../../ListMenu/TransactionListMenu";
import TransactionTransferStatus from "../../TransactionTransferStatus";
import TransactionAccount from "../../TransactionAccount";
import TransactionPaymentMethod from "../../TransactionPaymentMethod";
import {CreatedAtTd} from "../Common/CreatedAtTd";
import BukkuTooltip from "bukku/components/Tooltip";
import NumberCell from "bukku/components/Table/Cell/Number";
import TransactionLink from "../../TransactionLink";
import Tags from "../Cell/Tags";
import {TransactionColKey} from "../../../enums/TransactionColKey";
import { PopoverHomeAmount } from "../Common/PopoverHomeAmount";
import { getTransactionCustomFieldColumns } from "./getTransactionCustomFieldColumns";
import { FilesPreview } from "../../FilesPreview";
import { FileListModule } from "../../FileUploaderListManager/enum/FileListModule";
import ContactName from "bukku/components/Columns/Reports/Common/ContactName";

export function getTransactionColumnRender(key, props, fields) {
    const { openEmailDrawer, openModalShare } = props;

    // Custom Fields
    const fieldColMapper = {};
    const transactionFields = getTransactionCustomFieldColumns(fields);
    transactionFields.forEach(({key, render}) => {
        fieldColMapper[key] = render;
    });

    switch (key) {
        case TransactionColKey.number:
            return function (text, record) {
                return <NumberCell text={text} record={record} />;
            };

        case TransactionColKey.number2:
        case TransactionColKey.description:
        case TransactionColKey.created_by:
        case TransactionColKey.invoice_number:
        case TransactionColKey.invoice_description:
            return text => text || '-';
        case TransactionColKey.title:
            return function (text) {
                return (
                    <span>
                        <BukkuTooltip title={text} placement="topLeft">
                            {text || '-'}
                        </BukkuTooltip>
                    </span>
                );
            };

        case TransactionColKey.number_with_link:
            return function (text, record) {
                const {number} = record;

                return <TransactionLink transaction={record} label={number || '-'} />;
            };

        case TransactionColKey.type:
        case TransactionColKey.all_types:
            return function (text, record) {
                return <TransactionType record={record} isShort={key === TransactionColKey.type} />;
            };

        case TransactionColKey.date:
            return text => dayjs(text).format('DD/MM/YYYY');

        case TransactionColKey.contact_name:
            return function (text, record) {
                const {contact_id, contact_code} = record;
                return (
                    <ContactName
                        contact_id={contact_id}
                        contact_name={text}
                        contact_code={contact_code}
                    />
                );
            };
        case TransactionColKey.contact2_name:
            return function (text, record) {
                const {contact2_id, contact2_code} = record;
                return (
                    <ContactName
                        contact_id={contact2_id}
                        contact_name={text}
                        contact_code={contact2_code}
                    />
                );
            };
        case TransactionColKey.payment_account:
        case TransactionColKey.deposit_to:
        case TransactionColKey.pay_from:
        case TransactionColKey.transfer_from:
        case TransactionColKey.transfer_to:
            return function (account_id) {
                return <TransactionAccount accountId={account_id} />;
            };

        case TransactionColKey.payment_method:
            return function (text, record) {
                const {payment_method_name} = record;
                return <TransactionPaymentMethod paymentMethod={payment_method_name} />;
            };

        case TransactionColKey.amount:
            return function (text, record) {
                const {currency_symbol, amount, baseAmount} = record;
                return (
                    <PopoverHomeAmount title={<Trans>Amount</Trans>} amount={baseAmount} record={record}>
                        <AmountTd
                            currency_symbol={currency_symbol}
                            amount={amount}
                            doNotReadLists
                        />
                    </PopoverHomeAmount>
                );
            };

        case TransactionColKey.balance:
        case TransactionColKey.overpayment:
            return function (text, record) {
                const {currency_symbol, balance, baseBalance } = record;
                const getTitle = () => {
                    switch (key) {
                        case TransactionColKey.balance:
                            return <Trans>Balance</Trans>;
                        case TransactionColKey.overpayment:
                            return <Trans>Overpayment</Trans>;
                        default:
                            return null;
                    }
                };
                return (
                    <PopoverHomeAmount title={getTitle()} amount={baseBalance} record={record}>
                        <AmountTd
                            currency_symbol={currency_symbol}
                            amount={balance}
                            doNotReadLists
                        />
                    </PopoverHomeAmount>
                );
            };

        case TransactionColKey.status:
        case TransactionColKey.invoice_status:
            return function (text, record) {
                const {type} = record;
                switch (type) {
                    case Type.sale_quote:
                    case Type.sale_order:
                    case Type.sale_delivery_order:
                    case Type.sale_payment:
                    case Type.sale_refund:
                    case Type.purchase_order:
                    case Type.purchase_goods_received_note:
                    case Type.purchase_payment:
                    case Type.purchase_refund:
                    case Type.bank_income:
                    case Type.bank_expense:
                    case Type.bank_transfer:
                    case Type.journal_entry:
                    case Type.contra:
                    case Type.stock_adjustment:
                    case Type.stock_transfer:
                    case Type.tax_deemed_payment:
                        return <TransactionStatus
                            record={record}
                            onClick={e => e.stopPropagation()}
                        />;
                    case Type.sale_invoice:
                    case Type.opening_invoice:
                    case Type.sale_credit_note:
                    case Type.opening_sale_credit_note:
                    case Type.purchase_bill:
                    case Type.opening_bill:
                    case Type.purchase_credit_note:
                    case Type.opening_purchase_credit_note:
                        return <InvoiceTransactionStatus
                            record={record}
                            onClick={e => e.stopPropagation()}
                        />;
                    default:
                        throw Error(`(ColumnKey.status) Type is ${type}`);
                }
            };

        case TransactionColKey.tag_names:
            return function (value, record) {
                const { tag_names } = record;
                return <Tags list={tag_names} />;
            };

        case TransactionColKey.email:
            return function (value, record) {
                return <TransactionEmailStatus record={record} />;
            };

        case TransactionColKey.transfer:
            return function (value, record) {
                return <TransactionTransferStatus record={record} />;
            };

        case TransactionColKey.billing_party:
        case TransactionColKey.shipping_info:
        case TransactionColKey.shipping_party:
            return function (value) {
                return (
                    <div className="ellipsis-div">
                        {value ? (
                            <BukkuTooltip
                                placement="topLeft"
                                overlayInnerStyle={{whiteSpace: 'pre-line'}}
                            >
                                {value}
                            </BukkuTooltip>
                        ) : '-'}
                    </div>
                );
            };
        case TransactionColKey.created_at:
            return function (text) {
                return <CreatedAtTd text={text} />;
            };

        case TransactionColKey.files:
            return function (text, record) {
                return <FilesPreview type={FileListModule.TRANSACTION} record={record} />;
            };

        case TransactionColKey.action:
            return function (text, record) {
                const {type} = record;

                switch (type) {
                    case Type.sale_invoice:
                    case Type.opening_invoice:
                    case Type.sale_credit_note:
                    case Type.opening_sale_credit_note:
                    case Type.sale_quote:
                    case Type.sale_order:
                    case Type.sale_delivery_order:
                    case Type.sale_payment:
                    case Type.sale_refund:
                    case Type.purchase_order:
                    case Type.purchase_goods_received_note:
                    case Type.purchase_bill:
                    case Type.opening_bill:
                    case Type.purchase_credit_note:
                    case Type.opening_purchase_credit_note:
                    case Type.purchase_payment:
                    case Type.purchase_refund:
                    case Type.bank_income:
                    case Type.bank_expense:
                    case Type.bank_transfer:
                    case Type.journal_entry:
                    case Type.contra:
                    case Type.stock_adjustment:
                    case Type.stock_transfer:
                    case Type.tax_deemed_payment:

                        return <TransactionListMenu
                            type={type}
                            record={record}
                            openEmailDrawer={openEmailDrawer}
                            openShareModal={openModalShare}
                        />;
                    // case Type.tax_deemed_payment:
                    //     return <DeemedPaymentListMenu record={record} />;
                    default:
                        return null;
                    // throw Error("(ColumnKey.action) Type is "+record.type)
                }
            };
        default: {
            if (fieldColMapper[key])
                return fieldColMapper[key];
            throw Error(`No such Column Key ${key}`);
        }
    }
}
